import React, { FC, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import classNames from 'classnames';

import { useDeepCompareMemo } from '@clatter/platform';
import {
  NgCard,
  NgCardContent,
  NgCardTitle,
  WorkbookSampleDownload,
  NgRailItem,
  NgWorkbookUploadCard,
} from '../../../components';
import { aclRolesMap } from '../../../constants';
import { hasAccessByRole } from '../../../helpers';
import { CircularLoader } from '../../../shared';

interface LeftRailProps {
  grants?: UserGrants;
  isLoading?: boolean;
  workbookTargetKey?: string;
  projectData: any;
  projectSideNavItems: ProjectSideNavItem[];
  enableWorkbookUpload?: boolean;
  onWorkbookUploadComplete?: (data: IngestJob) => void;
}

const StyledContainer = styled.aside`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const StyledProjectSideNav = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

//@todo:
// refactor projectStepper slice to generic one and move this entries to it so we could
// benefit of functionality implemented in it (navigation, validation etc)...
interface ProjectSideNavProps {
  grants?: UserGrants;
  isLoading?: boolean;
  items: ProjectSideNavItem[];
}

const ProjectSideNav: FC<ProjectSideNavProps> = ({ items, grants, isLoading: isParentLoading }) => {
  const { projectId, slug } = useParams();
  const navigate = useNavigate();

  const isLoading = false; // @todo - fix parent loading when updating form

  const handleNavItemClick = useCallback((url: string) => {
    navigate(url);
  }, []);

  return (
    <NgCard>
      <NgCardContent>
        <StyledProjectSideNav>
          {isLoading ? (
            <CircularLoader />
          ) : (
            (items || []).map((item, index) => {
              if (
                item.accessRoles?.length &&
                !hasAccessByRole({
                  allowedRoles: item?.accessRoles,
                  role: grants?.role,
                })
              ) {
                return null;
              }

              const {
                submenu: { component: Component, accessRoles: submenuAccessRoles } = {},
                slug: itemSlug,
                url,
                displayName,
              } = item;

              if (
                Component &&
                submenuAccessRoles?.length &&
                hasAccessByRole({
                  allowedRoles: submenuAccessRoles,
                  role: grants?.role,
                })
              ) {
                return (
                  <React.Fragment key={index}>
                    <NgRailItem
                      title={displayName}
                      className={classNames({ active: slug === itemSlug })}
                      onClick={() => handleNavItemClick(url)}
                    ></NgRailItem>
                    {Component}
                  </React.Fragment>
                );
              }

              return (
                <NgRailItem
                  key={index}
                  title={displayName}
                  className={classNames({ active: slug === itemSlug })}
                  onClick={() => handleNavItemClick(url)}
                />
              );
            })
          )}
        </StyledProjectSideNav>
      </NgCardContent>
    </NgCard>
  );
};

export const ProjectsLeftRail: React.FC<LeftRailProps> = ({
  projectData: data,
  grants,
  isLoading,
  workbookTargetKey,
  projectSideNavItems,
  onWorkbookUploadComplete = () => {},
  enableWorkbookUpload,
}) => {
  const targetId = data?.ID;
  const sampleWorkbookUrl = useDeepCompareMemo(() => data?.ProjectTemplate?.SampleWorkbookUrl, [data]);

  const enableWorkbookActions = hasAccessByRole({ allowedRoles: [aclRolesMap.DATA_ADMIN], role: grants?.role });
  const isEtlEnabled = !!data?.ProjectTemplate?.HasEtl || data?.HasEtl;

  return (
    <StyledContainer>
      <ProjectSideNav items={projectSideNavItems} grants={grants} isLoading={isLoading} />

      {enableWorkbookUpload ||
        (isEtlEnabled && enableWorkbookActions && (
          <>
            <NgWorkbookUploadCard
              data={data}
              targetKey={workbookTargetKey}
              targetId={targetId}
              uploadFolderId={data.UploadedDataFolderID}
              onComplete={onWorkbookUploadComplete}
            />

            {isEtlEnabled && sampleWorkbookUrl && (
              <NgCard>
                <NgCardContent>
                  <NgCardTitle title="Sample Workbook" />

                  <WorkbookSampleDownload downloadUrl={sampleWorkbookUrl} />
                </NgCardContent>
              </NgCard>
            )}
          </>
        ))}
    </StyledContainer>
  );
};
